// Global packages and components
import { clsx } from 'clsx'
import { useAddressLookup } from '~/hooks'
import { LoaderIcon } from '~/components'

// Types
import type { CustomerAddressProps } from '~/types'

interface Props {
  label?: string
  labelSize?: 'sm' | 'md'
  disabled?: boolean
  address: string
  addressData: CustomerAddressProps | null
  dynamicWidth?: boolean
  fetcherKey?: string
  setAddress: (e: string) => void
  setAddressData: (e: CustomerAddressProps | null) => void
  onChangeAddress?: () => void
}

// Main export
const AddressFinder = ({
  label,
  labelSize,
  disabled,
  address,
  addressData,
  dynamicWidth = false,
  fetcherKey,
  setAddress,
  setAddressData,
  onChangeAddress,
}: Props) => {
  const { error, loading, suggestions, setSuggestions, getAddressData } =
    useAddressLookup({ address, addressData, fetcherKey, setAddressData })

  return (
    <div>
      <div className="mb-2 flex items-center">
        <label
          className={clsx('block font-medium', {
            'text-sm': labelSize == 'sm',
          })}
          htmlFor="field-address-finder"
        >
          {label ?? 'Your address'}
        </label>
        <div className="leading-none">
          {addressData && (
            <button
              onClick={() => {
                setAddress('')
                setAddressData(null)

                if (onChangeAddress) {
                  onChangeAddress()
                }
              }}
              className="ml-2 text-xs font-semibold text-teal-green underline"
            >
              Change address
            </button>
          )}
        </div>
      </div>

      <div className="relative">
        <div className="relative">
          <input
            data-cs-mask
            id="field-address-finder"
            type="search"
            className={clsx(
              'w-full rounded-lg border border-neutral-300 bg-white p-3 outline-none',
              {
                'md:min-w-72 lg:min-w-96': !dynamicWidth,
              }
            )}
            placeholder="Type your postcode"
            value={address}
            disabled={disabled || !!addressData}
            autoComplete="off"
            onChange={e => {
              setAddress(e.target.value)
            }}
            onFocus={() => {
              setAddress('')
              setAddressData(null)
            }}
          />

          {loading && (
            <div className="absolute right-6 top-1/2 -translate-y-1/2">
              <LoaderIcon size="xs" />
            </div>
          )}
        </div>

        {suggestions && suggestions.length > 0 && (
          <div
            data-cs-mask
            className="absolute left-0 top-full z-30 mb-8 max-h-60 w-full overflow-y-auto bg-white p-4 shadow-md"
          >
            {suggestions.map((s, i) => (
              <button
                key={s.id ?? i}
                className="block w-full border-b border-neutral-100 py-1.5 text-left last-of-type:border-0 hover:text-teal-green"
                onClick={() => {
                  // `label` is the formatted string we display (replaces address.io's `e.address`)
                  setAddress(s.label)
                  getAddressData(s.id)
                  setSuggestions([])
                }}
              >
                <span className="hover:cursor-pointer">{s.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <p className="mb-0 pt-2 text-xs text-red-600">{error}</p>}
    </div>
  )
}

export default AddressFinder
